import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';

import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import CircularProgress from '@zenbusiness/zui/CircularProgress';
import Divider from '@zenbusiness/zui/Divider';
import ErrorState from '@zenbusiness/zui/ErrorState';
import Grid from '@zenbusiness/zui/Grid';
import PageContainer from '@zenbusiness/zui/PageContainer';
import PageHeader from '@zenbusiness/zui/PageHeader';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Text from '@zenbusiness/zui/Text';

import BillingInfo from './components/BillingInfo';
import ChargesTable from './components/ChargesTable';
import Details from './components/Details';
import GetProductDescription from './components/GetProductDescription';
import IncludedInPlan from './components/IncludedInPlan';
import { getDisplayName } from './helpers/taskName';
import useOrderView from './hooks/useOrderView';
import * as Styled from './OrderView.style';
import { getOrderTransactionData } from './utils/orders';

const LOCALIZED_TEXT = {
  backButton: 'orderView.backButton',
  heading: 'orderView.heading',
  errorTitle: 'orderView.error.title',
  errorMessage: 'orderView.error.message',
  notFoundTitle: 'orderView.notFound.title',
  notFoundMessage: 'orderView.notFound.message',
  includedInPlanHeading: 'orderView.includedInPlan.heading',
  productDescriptionHeading: 'orderView.productDescription.heading',
  companyInfoHeading: 'orderView.companyInfo.heading',
  companyFormed: 'orderView.companyFormed',
  estimated: 'orderView.estimated',
  formationDate: 'orderView.formationDate',
  pending: 'orderView.pending',
};

const getFormation = (timeline) => {
  if (!Array.isArray(timeline) || timeline.length === 0) {
    return null;
  }
  const allEvents = timeline.flatMap((entry) => entry.events || []);
  const formedEvent = allEvents.find((event) => event.topic === 'formationFiled');
  const estimatedEvent = allEvents.find((event) => event.topic === 'estimatedFormation');
  const event = formedEvent || estimatedEvent;
  if (!event) {
    return null;
  }
  return {
    date: event.date,
    isFormed: !!formedEvent,
  };
};

function OrderViewBackButton({ onBack, fm }) {
  return (
    <Button
      variant="text"
      startIcon={<ChevronLeftRoundedIcon />}
      onClick={onBack}
      clickstreamProps={{ elementName: 'order_view_back_button' }}
    >
      {fm({ id: LOCALIZED_TEXT.backButton })}
    </Button>
  );
}

OrderViewBackButton.propTypes = {
  onBack: PropTypes.func.isRequired,
  fm: PropTypes.func.isRequired,
};

function OrderView() {
  const { formatMessage: fm } = useIntl();
  const { businessEntityUuid } = useParams();
  const navigate = useNavigate();

  const {
    loading,
    error,
    businessEntity: { businessEntityName, businessEntityType },
    account: { accountEmail, firstName, lastName },
    orderTasks,
    currentOrderTask,
    transactionItems,
    stockSetupData,
    timeline,
    isPlan,
    isBundle,
  } = useOrderView();

  const handleBack = () => navigate(`/dashboard/${businessEntityUuid}/subscription-hub/orders`);
  const startSlot = <OrderViewBackButton onBack={handleBack} fm={fm} />;

  if (error) {
    return (
      <PageContainer>
        <PageHeader title={fm({ id: LOCALIZED_TEXT.heading })} slots={{ start: startSlot }} />
        <ErrorState title={fm({ id: LOCALIZED_TEXT.errorTitle })} message={fm({ id: LOCALIZED_TEXT.errorMessage })} />
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title={fm({ id: LOCALIZED_TEXT.heading })} slots={{ start: startSlot }} />
        <Box display="flex" justifyContent="center" alignItems="center" padding={6}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (!currentOrderTask) {
    return (
      <PageContainer>
        <PageHeader title={fm({ id: LOCALIZED_TEXT.heading })} slots={{ start: startSlot }} />
        <ErrorState
          title={fm({ id: LOCALIZED_TEXT.notFoundTitle })}
          message={fm({ id: LOCALIZED_TEXT.notFoundMessage })}
        />
      </PageContainer>
    );
  }

  const { createdDatetime, termEndDate } = currentOrderTask;
  const formation = getFormation(timeline);

  const ProductDescription = GetProductDescription({
    orderTaskType: currentOrderTask.orderTaskType,
    orderTaskData: currentOrderTask.orderTaskData || {},
  });

  const { cardUsed, orderTransaction, transactions } = getOrderTransactionData(
    transactionItems,
    currentOrderTask,
    isPlan || isBundle,
  );

  const entityIcon =
    businessEntityType === 'llc' ? '/icon/product/llc-formation-256.svg' : '/icon/product/corp-formation-256.svg';

  return (
    <PageContainer>
      <PageHeader title={fm({ id: LOCALIZED_TEXT.heading })} slots={{ start: startSlot }} />
      <Styled.Root>
        <Text variant="h5" fontWeight="bold">
          {getDisplayName(currentOrderTask, businessEntityType, fm)}
        </Text>

        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} md={7}>
            <Details
              cardUsed={cardUsed}
              createdDatetime={createdDatetime}
              isPlan={isPlan || isBundle}
              loading={loading}
              termEndDate={termEndDate}
            />

            {(isPlan || isBundle || ProductDescription) && (
              <Styled.OverViewCard>
                <Grid container direction="column">
                  <Grid item>
                    <Text variant="body1" fontWeight="bold">
                      {isPlan || isBundle ? (
                        <FormattedMessage id={LOCALIZED_TEXT.includedInPlanHeading} />
                      ) : (
                        <FormattedMessage id={LOCALIZED_TEXT.productDescriptionHeading} />
                      )}
                    </Text>
                  </Grid>
                  <Grid item>
                    <Divider />
                  </Grid>
                  {isPlan || isBundle ? (
                    <IncludedInPlan loading={loading} orderTasks={orderTasks} />
                  ) : (
                    <Styled.SpacedItem item>{ProductDescription}</Styled.SpacedItem>
                  )}
                </Grid>
              </Styled.OverViewCard>
            )}

            <ChargesTable
              isPlan={isPlan || isBundle}
              loading={loading}
              orderTasks={orderTasks}
              orderTransaction={orderTransaction}
              primaryOrderTask={currentOrderTask}
              stockSetupData={stockSetupData}
              transactions={transactions}
            />
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={5}>
            {formation && (
              <Styled.OverViewCard>
                <Text variant="body1" fontWeight="bold">
                  <FormattedMessage id={LOCALIZED_TEXT.companyInfoHeading} />
                </Text>
                <Divider />
                {loading ? (
                  <Skeleton variant="rectangular" height={100} />
                ) : (
                  <Styled.SpacedItem container item>
                    <Styled.CompanyDetails container item spacing={2}>
                      <Grid item xs={2}>
                        <SharedAsset alt={businessEntityType} src={entityIcon} width={48} height={48} />
                      </Grid>
                      <Grid item xs={4}>
                        <Text variant="body1" fontWeight="bold">
                          <FormattedMessage id={LOCALIZED_TEXT.companyFormed} />
                        </Text>
                      </Grid>
                      <Grid item xs={4}>
                        <Text variant="body1">{businessEntityName}</Text>
                      </Grid>
                    </Styled.CompanyDetails>
                    <Grid container item spacing={2}>
                      <Grid item xs={2} />
                      <Grid item xs={4}>
                        <Text variant="body1" fontWeight="bold">
                          {formation.isFormed ? '' : fm({ id: LOCALIZED_TEXT.estimated })}
                          <FormattedMessage id={LOCALIZED_TEXT.formationDate} />
                        </Text>
                      </Grid>
                      <Grid item xs={4}>
                        <Text variant="body1">
                          {formation.date
                            ? format(parseISO(formation.date), 'MMMM d, yyyy')
                            : fm({ id: LOCALIZED_TEXT.pending })}
                        </Text>
                      </Grid>
                    </Grid>
                  </Styled.SpacedItem>
                )}
              </Styled.OverViewCard>
            )}

            {accountEmail && (
              <BillingInfo
                accountEmail={accountEmail}
                cardUsed={cardUsed}
                firstName={firstName}
                lastName={lastName}
                loading={loading}
              />
            )}
          </Grid>
        </Grid>
      </Styled.Root>
    </PageContainer>
  );
}

export default OrderView;
